import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import hhgLogo from '../assets/hhg-logo.png';
import { AccountDialog } from './AccountDialog';
import { OrderAlerts } from './OrderAlerts';
import { RechargeRequestAlerts } from './RechargeRequestAlerts';
import { Ps5VoiceAlerts } from './Ps5VoiceAlerts';
import { SoundToggle } from './SoundToggle';
import { Button } from './ui';

const LINK_BASE =
  'rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors';

function Tab({ to, children, block = false }: { to: string; children: string; block?: boolean }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${LINK_BASE} ${block ? 'flex min-h-11 items-center' : ''} ${
          isActive
            ? 'bg-sky-500/15 text-sky-300'
            : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="2"
    >
      {open ? (
        <path d="M6 6l12 12M18 6L6 18" />
      ) : (
        <path d="M4 7h16M4 12h16M4 17h16" />
      )}
    </svg>
  );
}

export function Layout() {
  const { staff, isAdmin, logout } = useAuth();
  const location = useLocation();
  // Phones only: the tabs live behind a menu button there. From tablet width
  // up they sit in the bar as before.
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Picking a page closes the menu, and so does Escape.
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // One list, rendered twice: inline on wide screens, stacked in the menu on
  // phones. Station setup and staff accounts are admin-only — the backend
  // enforces this too; the hidden tab is a convenience.
  const tabs = (block: boolean) => (
    <>
      <Tab to="/floor" block={block}>Floor</Tab>
      <Tab to="/customers" block={block}>Customers</Tab>
      <Tab to="/sessions" block={block}>History</Tab>
      <Tab to="/expenses" block={block}>Expenses</Tab>
      <Tab to="/staff-tab" block={block}>Staff tab</Tab>
      {isAdmin && <Tab to="/admin/reports" block={block}>Reports</Tab>}
      {isAdmin && <Tab to="/admin/stations" block={block}>Stations</Tab>}
      {isAdmin && <Tab to="/admin/recharge-packs" block={block}>Recharge packs</Tab>}
      {isAdmin && <Tab to="/admin/menu" block={block}>Menu</Tab>}
      {isAdmin && <Tab to="/admin/staff" block={block}>Staff</Tab>}
      {isAdmin && <Tab to="/admin/recharge-edits" block={block}>Recharge edits</Tab>}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img
              src={hhgLogo}
              alt="Happy Hours Gaming"
              className="h-10 w-10 rounded-lg object-cover"
            />
            <span className="text-base font-semibold tracking-tight">
              HHG <span className="text-sky-400">Manager</span>
            </span>
          </div>

          <nav className="-mx-1 hidden flex-1 items-center gap-1 overflow-x-auto px-1 md:flex">
            {tabs(false)}
          </nav>

          <div className="ml-auto flex items-center gap-3 md:ml-0">
            {/* Always in view, on every screen size: a muted or not-yet-tapped
                device is why a PS5 alert would go unheard. */}
            <SoundToggle />
            {/* The name opens "My account": change your own username and password. */}
            <div className="hidden md:block">
              <Button
                variant="ghost"
                title="Change your username or password"
                onClick={() => setAccountOpen(true)}
              >
                {staff?.name}
                {isAdmin && <span className="text-xs text-sky-400">admin</span>}
              </Button>
            </div>
            {/* Wrapped, because Button's own inline-flex would beat a
                hidden/md:hidden put on the button itself. */}
            <div className="hidden md:block">
              <Button variant="secondary" onClick={logout}>
                Sign out
              </Button>
            </div>
            <div className="md:hidden">
              <Button
                variant="ghost"
                className="px-2.5"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav"
                onClick={() => setMenuOpen((open) => !open)}
              >
                <MenuIcon open={menuOpen} />
              </Button>
            </div>
          </div>
        </div>

        {menuOpen && (
          <div
            id="mobile-nav"
            className="max-h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-slate-800 px-4 pb-4 pt-2 md:hidden"
          >
            <nav className="flex flex-col gap-1">{tabs(true)}</nav>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-800 pt-3">
              <Button
                variant="ghost"
                className="px-2"
                onClick={() => {
                  setMenuOpen(false);
                  setAccountOpen(true);
                }}
              >
                {staff?.name}
                {isAdmin && <span className="text-xs text-sky-400">admin</span>}
                <span className="text-xs text-slate-500">· My account</span>
              </Button>
              <Button variant="secondary" onClick={logout}>
                Sign out
              </Button>
            </div>
          </div>
        )}

        {/* Inside the sticky header, so an order from a station stays in view
            on every page and at every scroll position until it is dealt
            with. See the comment at the top of OrderAlerts. */}
        <OrderAlerts />
        <RechargeRequestAlerts />
        <Ps5VoiceAlerts />
      </header>

      {/* Tapping the page behind an open menu closes it. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 md:hidden"
          aria-hidden="true"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <main className="px-4 py-5">
        <Outlet />
      </main>

      {accountOpen && <AccountDialog onClose={() => setAccountOpen(false)} />}
    </div>
  );
}
